import { Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { environment } from '@environments/environment';
import { clone } from '@helpers/arrays';
import { MenuItem } from '@models/menu-item';
import { AppConfigProvider } from '@root/app/app.config.provider';
import { OpenIdConnectService } from '@services/auth/open-id-connect.service';
import { PermissionsService } from '@services/auth/permissions.service';
import { DatasetsService } from '@services/datasets/datasets.service';
import { ResultsStoreService } from '@services/results/results-store.service';
import { ApplicationStatus } from '@services/results/application-status';
import { SimulationProgress, SimulationRunnerService } from '@services/simulation-set/simulation-runner.service';
import { TrackingService } from '@services/tracking/tracking.service';
import { Subject } from 'rxjs';
import { catchError, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import Swal, { SweetAlertOptions } from 'sweetalert2';

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
})
export class HeaderComponent implements OnInit, OnDestroy {
  @ViewChild('globalSaveButton', { static: false }) globalSaveButton: ElementRef;
  @ViewChild('quickLinks', { static: false }) quickLinks: ElementRef;
  @ViewChild('userPanel', { static: false }) userPanel: ElementRef;
  @Input() runComplete;
  @Output() saveEvent = new EventEmitter();
  @Output() discardEvent = new EventEmitter();

  private appHasChanges = false;
  private onDestroy$ = new Subject();

  private wasInside = false;

  public activeItem: MenuItem;
  public isProduction = environment.production;
  public appVersion;
  public dialogLoading = false;
  public isSaving = false;
  public isDiscarding = false;
  public links: MenuItem[];
  public menuItems: MenuItem[];
  public quickLinksOpen = false;
  public userAreaOpen = false;
  public progress: SimulationProgress;
  private userRoleHasAccessToImport;
  private userRoleHasAccessToSave;
  private userRoleHasAccessToSimulation;

  public get isSaveButtonEnabled() {
    return this.appHasChanges && !this.progress.isRunning && this.userRoleHasAccessToSave;
  }

  public get isSimulationButtonEnabled() {
    return this.progress && !this.progress.isRunning && this.userRoleHasAccessToSimulation;
  }

  public get isImportRequired() {
    return this.dataSetService.isImportRequired;
  }

  public get isImportButtonEnabled() {
    return this.progress && !this.progress.isRunning && this.userRoleHasAccessToImport;
  }

  public get isDiscardButtonEnabled() {
    return this.progress && !this.progress.isRunning && this.appHasChanges;
  }

  constructor(
    private dataSetService: DatasetsService,
    private router: Router,
    private runner: SimulationRunnerService,
    private store: ResultsStoreService,
    private tracking: TrackingService,
    public dialog: MatDialog,
    public openIdConnectService: OpenIdConnectService,
    private permissionService: PermissionsService,
    private appConfig: AppConfigProvider
  ) {
    this.appConfig.environment.subscribe(env => {
      this.appVersion = env.VERSION.BUILDNUMBER;
    });
  }

  ngOnInit() {
    this.userRoleHasAccessToImport = this.permissionService.getPermission('Import') === 'W';
    this.userRoleHasAccessToSave = this.permissionService.getPermission('ManageResultsets') === 'W';
    this.userRoleHasAccessToSimulation = this.permissionService.getPermission('RunSimulation') === 'W';
    this.links = [];
    for (const route of this.router.config) {
      if (route.path && route.component) {
        this.links.push({
          routerLink: `/${route.path}`,
          label: route.path.toUpperCase(),
          canDeactivate: true,
        });
      }
    }
    this.activeItem = this.links[0];
    this.runComplete = false;

    this.store.appStatus$.pipe(takeUntil(this.onDestroy$)).subscribe((status: ApplicationStatus) => {
      switch (status) {
        case ApplicationStatus.RunningSimulation:
          // this.appStatusAllowsSave = false;
          break;
        case ApplicationStatus.DoneSimulation:
          // this.appStatusAllowsSave = false;
          this.isSaving = false;
          this.isDiscarding = false;
          break;
        case ApplicationStatus.SavingState:
          this.isSaving = true;
          break;
        case ApplicationStatus.DoneSavingState:
          this.isSaving = false;
          break;
      }
    });

    this.runner.progress$.subscribe(progress => {
      this.progress = clone(progress);
    });

    this.tracking.modelDataHasChanges$.pipe(takeUntil(this.onDestroy$), distinctUntilChanged()).subscribe(status => {
      const result = Object.values(status).reduce((current, accumulator) => current || accumulator, false);
      this.appHasChanges = result;
    });

    this.hideDropdownOnClickOutside();
  }

  ngOnDestroy() {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  private hideDropdownOnClickOutside() {
    const outsideClickListener = event => {
      const inside = this.quickLinks.nativeElement.contains(event.target) || this.userPanel.nativeElement.contains(event.target);
      if (!inside) {
        this.closeAllDropdowns();
      }
    };

    document.addEventListener('click', outsideClickListener);
  }

  public closeAllDropdowns() {
    this.quickLinksOpen = false;
    this.userAreaOpen = false;
  }

  public toggleUserArea() {
    this.userAreaOpen = !this.userAreaOpen;
    if (this.userAreaOpen) {
      this.quickLinksOpen = false;
    }
  }

  public toggleQuickLinks() {
    this.quickLinksOpen = !this.quickLinksOpen;
    if (this.quickLinksOpen) {
      this.userAreaOpen = false;
    }
  }

  public saveResults(e: Event) {
    if (this.isSaving || this.isDiscarding) {
      e.preventDefault();
    } else {
      this.closeAllDropdowns();
      this.saveEvent.emit(null);
    }
  }

  public discardResults(e: Event) {
    if (this.isSaving || this.isDiscarding) {
      e.preventDefault();
    } else {
      this.closeAllDropdowns();
      this.discardEvent.emit(null);

      const text = 'Are you sure you want to discard <strong><em>all unsaved changes</em></strong>?';
      const title = 'Discard Unsaved Changes';
      const confirmButtonText = 'Discard';
      const cancelButtonText = 'Cancel';
      const confirmButtonColor = '#A9A9A9';
      const cancelButtonColor = '#3085d6';

      Swal.fire({
        title: title,
        html: text,
        type: 'warning',
        showCancelButton: true,
        confirmButtonText,
        cancelButtonText,
        reverseButtons: true,
        allowEscapeKey: true,
        confirmButtonColor, // If =null, use default color
        cancelButtonColor, // If =null, use default color
      } as SweetAlertOptions).then(res => {
        if (res && res.value) {
          this.isDiscarding = true;
          this.tracking
            .deleteUserTracking(this.openIdConnectService.userId)
            .pipe(
              catchError(err => {
                this.isDiscarding = false;
                return err;
              })
            )
            .subscribe(res => {
              const trackingData = this.tracking.modelDataHasChanges$.getValue();
              if (trackingData.resultsets) {
                this.store.loadDataFromDB(true, false, false).finally(() => {
                  this.isDiscarding = false;
                  this.tracking.setAllModelDataChangesAfterSave();
                });
              } else {
                this.isDiscarding = false;
                this.tracking.setAllModelDataChangesAfterSave();
              }
            });
        }
      });
    }
  }

  public getImportFlagPreDialog() {
    this.closeAllDropdowns();
    this.dialogLoading = true;
    this.dataSetService.getRequiredDatasetImport().then(_ => (this.dialogLoading = false));
  }
}
